/**
 * Storage Manager
 * 
 * Manages localStorage data for anonymous and authenticated users.
 * Ensures proper separation between anonymous and authenticated user sessions.
 */

/**
 * Keys used by anonymous users that should be cleared on login
 */
const ANONYMOUS_USER_KEYS = [
  'cart',
  'wishlist',
  'liked',
  'checkoutFormStateV1',
] as const

/**
 * Keys used by authenticated users that should be preserved
 */
const AUTH_USER_KEYS = [
  'accessToken',
  'refreshToken',
  'idToken',
  'username',
  'cognitoUser',
] as const

/**
 * Clear all anonymous user data from localStorage
 * This should be called when a user successfully logs in
 * to prevent anonymous cart/wishlist items from persisting
 */
export const clearAnonymousUserData = (): void => {
  try {
    ANONYMOUS_USER_KEYS.forEach(key => {
      localStorage.removeItem(key)
    })
  } catch (error) {
    // Handle error silently
  }
}

/**
 * Clear all authenticated user data from localStorage
 * This should be called when a user logs out
 */
export const clearAuthenticatedUserData = (): void => {
  try {
    AUTH_USER_KEYS.forEach(key => {
      localStorage.removeItem(key)
    })
  } catch (error) {
    // Handle error silently
  }
}

/**
 * Clear all user-related data from localStorage
 * This is a complete reset of both anonymous and authenticated data
 */
export const clearAllUserData = (): void => {
  clearAnonymousUserData()
  clearAuthenticatedUserData()
}

/**
 * Get the current storage mode based on authentication state
 */
export const getStorageMode = (): 'anonymous' | 'authenticated' => {
  const accessToken = localStorage.getItem('accessToken')
  const username = localStorage.getItem('username')
  
  return accessToken && username ? 'authenticated' : 'anonymous'
}

